import React, { useState } from "react";
import { careNotesLabel, careSectionLabels, careTitle } from "./careCopy";
import type { CareLang, ResolvedCare } from "./careTypes";
import { careLines, isBlankText, pickText } from "./careValues";
import "./care.scss";

type Props = {
  care?: ResolvedCare | null;
  lang: CareLang;
  /** Certificate: sections open and close; previews show everything. */
  collapsible?: boolean;
  /** Hide the "Care & Preservation" heading when the host already shows one. */
  hideTitle?: boolean;
};

/**
 * The one rendering of resolved care, shared by the certificate, the studio preview and the print card, so
 * buyers and studios always see the same thing. Text is rendered as text, never as HTML.
 */
const CareView = ({ care, lang, collapsible = false, hideTitle = false }: Props) => {
  const sections = care?.sections || [];
  const [open, setOpen] = useState<string | null>(sections[0]?.key || null);
  if (!sections.length && isBlankText(care?.additionalNotes)) return null;
  const notes = pickText(care?.additionalNotes, lang);

  return (
    <section className="hkp-care" lang={lang} aria-labelledby={hideTitle ? undefined : "hkp-care-title"}>
      {!hideTitle && (
        <h2 id="hkp-care-title" className="hkp-care__title">
          {careTitle[lang]}
        </h2>
      )}
      {notes && (
        <div className="hkp-care__notes">
          <h3>{careNotesLabel[lang]}</h3>
          {careLines(notes).map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
      )}
      <div className="hkp-care__sections">
        {sections.map((section) => {
          const label = careSectionLabels[section.key]?.[lang] || section.key;
          const lines = careLines(pickText(section, lang));
          if (!lines.length) return null;
          const body = (
            <ul className="hkp-care__list">
              {lines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          );
          if (!collapsible) {
            return (
              <div className="hkp-care__section" key={section.key}>
                <h3>{label}</h3>
                {body}
              </div>
            );
          }
          const isOpen = open === section.key;
          const panelId = `hkp-care-${section.key}`;
          return (
            <div className={`hkp-care__section is-collapsible${isOpen ? " is-open" : ""}`} key={section.key}>
              <h3>
                <button type="button" aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpen(isOpen ? null : section.key)}>
                  <span>{label}</span>
                  <span className="hkp-care__chevron" aria-hidden="true" />
                </button>
              </h3>
              <div id={panelId} hidden={!isOpen}>
                {body}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default CareView;
